import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { IAuthData } from '../types/auth-data.interface';
import { selectAuthData } from '../store/account/reducer';
import {
  catchError,
  map,
  mergeMap,
  take,
  throwError,
  retry,
  timer,
} from 'rxjs';
import { accountActions } from '../store/account/actions';
import { AccountService } from '../store/account/services/account.service';
import { NGXLogger } from 'ngx-logger';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(Store);
  const logger = inject(NGXLogger);
  const accountService = inject(AccountService);
  const MAX_RETRIES = 3;

  return store.select(selectAuthData).pipe(
    take(1),
    mergeMap((authData: IAuthData | null) => {
      if (authData) {
        req = req.clone({
          setHeaders: {
            Authorization: `${authData.token_type} ${authData.access_token}`,
          },
        });
      }
      return next(req).pipe(
        retry({
          count: MAX_RETRIES,
          delay: (error, retryCount) => {
            if (
              error.status !== 401 &&
              !(
                error.status === 400 &&
                error.error &&
                error.error.error === 'invalid_grant'
              )
            ) {
              logger.error(
                `Request to ${req.url} failed with status ${error.status}, retrying attempt ${retryCount}/${MAX_RETRIES}`,
                error
              );
              return timer(1000 * retryCount);
            }
            return throwError(() => error);
          },
        }),
        catchError((error) => {
          if (
            (error.status === 401 || error.error.code === 401) &&
            authData &&
            error.error.message !== 'The refresh token is invalid.'
          ) {
            return accountService.refreshAuthData(authData).pipe(
              map((newAuthData) => {
                store.dispatch(
                  accountActions.refreshAuthDataSuccess({
                    authData: newAuthData,
                  })
                );
                return newAuthData;
              }),
              mergeMap((newAuthData) => {
                return next(
                  req.clone({
                    setHeaders: {
                      Authorization: `${newAuthData.token_type} ${newAuthData.access_token}`,
                    },
                  })
                );
              })
            );
          }

          // Handle all errors after retries have failed
          logger.error(
            `Request to ${req.url} failed with status ${error.status} after all retry attempts:`,
            error
          );

          return throwError(() => error);
        })
      );
    })
  );
};
